import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X, ArrowRight } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { Photo, CategoryIcon, usePriceText } from "@/components/dm/ui";

const weekKey = (hubId) => {
  const d = new Date();
  const start = new Date(d.getFullYear(), 0, 1);
  return `${hubId}:${d.getFullYear()}-${Math.ceil(((d - start) / 86400000 + start.getDay() + 1) / 7)}`;
};

const MiniPost = ({ p }) => {
  const { cat } = useApp();
  const price = usePriceText()(p);
  const isReq = p.type === "request";
  return (
    <Link to={`/post/${p.id}`} data-testid={`digest-post-${p.id}`} className="press shrink-0 w-36 rounded-2xl bg-[#FFFDF9] overflow-hidden text-[#291E18]">
      {isReq || !p.photos?.length ? (
        <div className={`h-20 flex items-center justify-center ${isReq ? "bg-[#FFF4EA] border-b-4 border-[#C2410C]" : "bg-[#F3EDE4]"}`}><CategoryIcon icon={cat(p.category_id)?.icon} className="w-7 h-7 text-[#C2410C]" /></div>
      ) : <Photo src={p.photos[0]} alt={p.title} icon={cat(p.category_id)?.icon} className="h-20 w-full" />}
      <div className="p-2"><div className="text-xs font-bold line-clamp-2 leading-snug">{p.title}</div>{price && <div className="text-xs font-extrabold text-[#92400E] mt-0.5">{price}</div>}</div>
    </Link>
  );
};

export const WeeklyDigest = ({ hub }) => {
  const { t, L, cat } = useApp();
  const [d, setD] = useState(null);
  const key = weekKey(hub.id);
  const [hidden, setHidden] = useState(() => localStorage.getItem("dm_digest_hidden") === key);
  useEffect(() => { if (!hidden) api.get("/digest", { params: { hub: hub.id } }).then((r) => setD(r.data)).catch(() => {}); }, [hub.id, hidden]);
  if (hidden || !d) return null;
  const hide = () => { localStorage.setItem("dm_digest_hidden", key); setHidden(true); };
  const city = L(hub);
  const quiet = d.listings + d.requests === 0;
  return (
    <section data-testid="weekly-digest" className="relative rounded-[24px] bg-[#1E4D3B] text-white p-5 overflow-hidden card-shadow">
      <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full ankara-dots opacity-30" aria-hidden="true" />
      <button data-testid="digest-hide" onClick={hide} aria-label={t("digest_hide")} className="press absolute top-3 right-3 w-11 h-11 rounded-full bg-white/10 flex items-center justify-center"><X className="w-5 h-5" /></button>
      <h2 className="text-lg md:text-xl font-extrabold inline-flex items-center gap-2 pr-12"><Sparkles className="w-5 h-5 text-[#F59E0B]" aria-hidden="true" />{t("digest_title", { city })}</h2>
      {quiet ? <p className="mt-2 text-white/85" data-testid="digest-quiet">{t("digest_quiet", { city })}</p> : (<>
        <div className="mt-3 flex flex-wrap gap-2" data-testid="digest-counts">
          {d.listings > 0 && <span className="rounded-full bg-[#F59E0B] text-[#291E18] px-3 py-1 text-sm font-extrabold">{d.listings === 1 ? t("digest_listing_1") : t("digest_listings", { n: d.listings })}</span>}
          {d.requests > 0 && <span className="rounded-full bg-[#C2410C] text-white px-3 py-1 text-sm font-extrabold">{d.requests === 1 ? t("digest_request_1") : t("digest_requests", { n: d.requests })}</span>}
        </div>
        {d.top_categories.length > 0 && (
          <p className="mt-3 text-sm text-white/85 flex flex-wrap items-center gap-x-2 gap-y-1"><span className="font-bold text-[#FDE68A]">{t("digest_top")}</span>
            {d.top_categories.map((c) => <span key={c.id} className="inline-flex items-center gap-1"><CategoryIcon icon={cat(c.id)?.icon} className="w-3.5 h-3.5" />{L(cat(c.id))} ({c.n})</span>)}</p>
        )}
        <div className="mt-4 flex gap-3 overflow-x-auto no-scrollbar -mx-5 px-5">{d.posts.map((p) => <MiniPost key={p.id} p={p} />)}</div>
        <Link to="/explore" data-testid="digest-see-all" className="press mt-4 inline-flex items-center gap-1 min-h-[44px] font-extrabold text-[#F59E0B]">{t("digest_see_all")}<ArrowRight className="w-4 h-4" /></Link>
      </>)}
    </section>
  );
};
